import React, { useEffect, useState, useRef } from 'react';
import { useParams } from '@tanstack/react-router';
import { QRCodeSVG } from 'qrcode.react';
import { employeeshipsApi } from '../../services/api';
import { Loader2, Download, CheckCircle2, Award, Printer } from 'lucide-react';
import logo from '../../assets/branding/logo-short.png';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const CertificateViewer = () => {
  const { certificateId } = useParams({ from: '/employee-certificates/$certificateId' });
  const [internship, setInternship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const certificateRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!certificateRef.current) return;
    try {
      setDownloading(true);
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2, 
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: certificateRef.current.scrollWidth,
        windowHeight: certificateRef.current.scrollHeight
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
      const marginY = (pdfHeight - imgHeight) / 2;
      
      pdf.addImage(imgData, 'JPEG', 0, marginY > 0 ? marginY : 0, pdfWidth, imgHeight);
      pdf.save(`HARA_XY_Certificate_${internship.certificateId}.pdf`);
    } catch (err) {
      console.error('Failed to generate PDF', err);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchInternship = async () => {
      try {
        setLoading(true);
        const response = await employeeshipsApi.getCertificate(certificateId);
        setInternship(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Certificate not found');
      } finally {
        setLoading(false);
      }
    };

    fetchInternship();
  }, [certificateId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400 font-medium tracking-wide">Verifying Certificate...</p>
        </div>
      </div>
    );
  }

  if (error || !internship) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="max-w-md w-full p-8 bg-black rounded-2xl shadow-2xl border border-gray-800 text-center">
          <div className="w-16 h-16 bg-gray-900 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold text-gray-200 mb-2">Invalid Certificate</h2>
          <p className="text-gray-400 mb-8">{error || 'This certificate record could not be found.'}</p>
          <a href="/" className="inline-block px-6 py-3 bg-gray-200 text-black rounded-lg font-semibold hover:bg-white transition">
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative py-16 px-4 flex flex-col items-center overflow-hidden">
      {/* Decorative ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vh] bg-gray-700 opacity-[0.03] rounded-full blur-[120px] pointer-events-none -z-10"></div>
      
      {/* Grid pattern overlay for texture */}
      <div className="absolute inset-0 opacity-[0.15] -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
        <div className="grid grid-cols-8 grid-rows-6 h-full w-full">
          {Array.from({ length: 48 }).map((_, i) => (
            <div key={i} className="border border-white/10"></div>
          ))}
        </div>
      </div>

      {/* Mobile Wrapper */}
      <div className="w-full max-w-full overflow-x-auto flex justify-center pb-8 px-4">
        {/* Certificate Container */}
        <div 
          ref={certificateRef}
          className="bg-white w-[850px] min-w-[850px] min-h-[600px] shadow-[0_0_50px_rgba(255,255,255,0.1)] rounded-xl overflow-hidden relative border border-gray-800 shrink-0"
          style={{ fontFamily: "'Roboto', sans-serif" }}
        >
          {/* Header Strip */}
          <div className="bg-black text-gray-300 py-8 px-6 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gray-400"></div>
            
            {/* Logo */}
            <div className="absolute top-6 left-6 hidden md:block">
              <img src={logo} alt="HARA-XY Logo" className="h-12 w-auto brightness-0 invert opacity-90" />
            </div>

            <div className="px-16 md:px-28">
              <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-1 uppercase text-white">INTERNSHIP CERTIFICATE</h1>
              <h2 className="text-lg md:text-xl font-light opacity-80 tracking-widest uppercase">Hara XY Innovation Lab</h2>
            </div>
            
            {/* QR Code */}
            <div className="absolute top-6 right-6 bg-white p-1.5 rounded shadow-sm hidden md:block">
              <QRCodeSVG 
                value={`https://hara-xy.com/employee-certificates/${internship.certificateId}`}
                size={70}
                level="H"
              />
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8 md:p-12 flex flex-col md:flex-row gap-8 relative">
            <div className="flex-1 space-y-6">
              <p className="text-center text-gray-500 uppercase tracking-widest text-sm">This is to certify that</p>
              <h3 className="text-4xl md:text-5xl font-black text-black text-center my-2 uppercase tracking-tight">
                {internship.name}
              </h3>
              
              {internship.studentDetails?.college && (
                <div className="text-center text-gray-500 text-sm font-medium mb-4 uppercase tracking-wider">
                  {internship.studentDetails.college} | Reg No: {internship.studentDetails.registrationNumber}
                </div>
              )}
              
              <div className="text-center text-gray-800">
                <p className="mb-2 text-lg">
                  has successfully completed the internship program from<br/><strong>{formatDate(internship.startDate)}</strong> to <strong>{formatDate(internship.endDate)}</strong>
                </p>
                <p className="text-xl font-bold text-black mb-6 uppercase tracking-wider mt-4">
                  Role: {internship.role}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-1">
                {/* Skills Acquired */}
                <div className="group relative bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <h4 className="text-black font-bold text-xs tracking-widest uppercase mb-2 flex items-center gap-2">
                    <Award className="w-3.5 h-3.5 text-gray-400" />
                    Key Skills
                  </h4>
                  {internship.skills.length > 10 ? (
                    <div className="text-[10px] font-bold text-gray-700 leading-relaxed tracking-widest uppercase">
                      {internship.skills.join(' • ')}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {internship.skills.map((skill, index) => (
                        <span 
                          key={index} 
                          className="px-2 py-1 bg-white text-gray-800 border border-gray-200 rounded text-xs font-semibold tracking-wide uppercase flex items-center gap-1.5 shadow-sm"
                        >
                          <span className="w-1 h-1 rounded-full bg-black"></span>
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Achievements */}
                {internship.studentDetails?.achievements && (
                  <div className="group relative bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <h4 className="text-black font-bold text-xs tracking-widest uppercase mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-gray-400" />
                      Achievements
                    </h4>
                    <ul className="space-y-1.5">
                      {internship.studentDetails.achievements.map((achievement, index) => (
                        <li key={index} className="flex items-start gap-1.5 text-gray-800 text-xs leading-relaxed font-medium">
                          <div className="mt-[2px] text-black flex-shrink-0">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                          </div>
                          <span>{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <p className="text-gray-600 leading-relaxed text-center mt-8 text-sm">
                demonstrated exceptional dedication, professionalism, and an eagerness to learn. 
                Contributed significantly to team projects and gained valuable practical experience.
              </p>
            </div>

            {/* Signature/Seal Side Section */}
            <div className="md:w-64 flex flex-col justify-between items-center py-6 border-t md:border-t-0 md:border-l border-gray-200 pl-0 md:pl-8">
              <div className="text-center space-y-2 mt-auto">
                <div className="mx-auto mb-1 flex justify-center h-16 w-32 border-b border-gray-200 pb-1">
                  <img src="/branding/signature.png" alt="Signature" className="h-full w-full object-contain" />
                </div>
                <p className="font-black text-black text-sm uppercase tracking-wider">Shivangouda R. Patil</p>
                <p className="text-gray-500 text-xs uppercase tracking-widest">Managing Director</p>
                <p className="text-gray-400 text-xs mt-2 font-mono">{formatDate(internship.issueDate)}</p>
              </div>

              {/* Corporate Seal */}
              <div className="mt-10 relative group">
                <div className="w-28 h-28 rounded-full bg-transparent flex items-center justify-center border-[2px] border-black relative mix-blend-multiply opacity-90">
                  {/* Inner dashed ring */}
                  <div className="absolute inset-[5px] border-[1.5px] border-dashed border-black rounded-full opacity-60"></div>
                  
                  {/* Spinning Text Ring (CSS based for PDF compatibility) */}
                  <div className="absolute inset-0 w-full h-full animate-[spin_30s_linear_infinite] rounded-full">
                    {"HARA XY INNOVATION LAB PVT LTD ★ ".split('').map((char, i, arr) => (
                      <span
                        key={i}
                        className="absolute left-[50%] top-[4px] text-[10px] font-bold text-black uppercase origin-[center_50px]"
                        style={{
                          transform: `translateX(-50%) rotate(${i * (360 / arr.length)}deg)`,
                        }}
                      >
                        {char}
                      </span>
                    ))}
                  </div>
                  
                  {/* Center Logo */}
                  <div className="text-center z-10 flex flex-col items-center bg-white rounded-full p-1.5">
                    <span className="text-[16px] font-black text-black select-none tracking-tighter leading-none">HARA</span>
                    <span className="text-[16px] font-black text-black select-none tracking-tighter leading-none">XY</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 py-5 px-12 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400 font-mono tracking-widest uppercase">
              Certificate ID: <span className="text-black font-bold ml-2">{internship.certificateId}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex flex-wrap justify-center gap-5 no-print relative z-10">
        <button 
          onClick={handleDownloadPDF}
          disabled={downloading}
          className={`flex items-center gap-2 px-8 py-3 bg-gray-200 text-black rounded font-bold transition-all tracking-wide uppercase text-sm ${downloading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-white shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_25px_rgba(255,255,255,0.2)]'}`}
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {downloading ? 'Generating PDF...' : 'Download PDF Certificate'}
        </button>
      </div>

      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 0; }
          body { margin: 0 !important; padding: 0 !important; background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          
          /* Reset outer wrappers */
          .min-h-screen { height: auto !important; min-height: 0 !important; display: block !important; padding: 0 !important; overflow: visible !important; }
          
          /* Exact A4 dimensions for the certificate */
          .w-\\[850px\\] { 
            width: 297mm !important; 
            height: 209mm !important; 
            max-width: none !important; 
            min-width: 0 !important; 
            min-height: 0 !important; 
            margin: 0 !important;
            box-shadow: none !important; 
            border: none !important; 
            border-radius: 0 !important;
            transform: none !important;
            page-break-inside: avoid;
          }
          
          /* Remove ambient glow to prevent overflow */
          .absolute.w-\\[80vw\\] { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default CertificateViewer;
